import { Input } from '../../../../../../../components';
import { createEmptyStringEntry } from '../utils';
import type { StringEntry } from '../types';
import {
  AddEntryButton,
  DEFAULT_MAX_ITEMS_HEIGHT_CLASSNAME,
  DUPLICATE_VALUE_ERROR,
  EMPTY_VALUE_ERROR,
  EmptyHint,
  EntriesScrollArea,
  FieldError,
  RemoveEntryButton,
  getDuplicateTrimmedValues,
  removeEntryById,
  updateEntryById,
} from './shared';

type StringListProps = {
  entries: StringEntry[];
  onChange: (entries: StringEntry[]) => void;
  addLabel: string;
  placeholder?: string;
  emptyHint?: string;
  maxItemsHeightClassName?: string;
};

export const getValueError = (
  trimmedValue: string,
  duplicateValues: ReadonlySet<string>
): string | undefined => {
  if (!trimmedValue) return EMPTY_VALUE_ERROR;
  if (duplicateValues.has(trimmedValue)) return DUPLICATE_VALUE_ERROR;
  return undefined;
};

type EntryRowProps = {
  entry: StringEntry;
  error?: string;
  placeholder: string;
  onValueChange: (nextValue: string) => void;
  onRemove: () => void;
};

const EntryRow: React.FC<EntryRowProps> = ({
  entry,
  error,
  placeholder,
  onValueChange,
  onRemove,
}) => (
  <div className="flex flex-col gap-y-1 w-full">
    <div className="flex flex-row items-stretch gap-x-2 w-full">
      <div className="flex-1">
        <Input
          value={entry.value}
          placeholder={placeholder}
          defaultSizeClass="w-full"
          onChange={onValueChange}
        />
      </div>
      <RemoveEntryButton onClick={onRemove} />
    </div>
    <FieldError message={error} />
  </div>
);

export const StringList: React.FC<StringListProps> = ({
  entries,
  onChange,
  addLabel,
  placeholder = 'Value',
  emptyHint,
  maxItemsHeightClassName = DEFAULT_MAX_ITEMS_HEIGHT_CLASSNAME,
}) => {
  const duplicateValues = getDuplicateTrimmedValues(
    entries.map(entry => entry.value)
  );
  const hasEntries = entries.length > 0;

  const handleValueChange = (id: string, nextValue: string) => {
    onChange(updateEntryById(entries, id, { value: nextValue }));
  };

  const handleRemove = (id: string) => {
    onChange(removeEntryById(entries, id));
  };

  const handleAdd = () => {
    onChange([...entries, createEmptyStringEntry()]);
  };

  return (
    <div className="flex flex-col gap-y-3 w-full">
      {!hasEntries && <EmptyHint message={emptyHint} />}

      <EntriesScrollArea
        hasEntries={hasEntries}
        maxHeightClassName={maxItemsHeightClassName}
        gapClassName="gap-y-2"
      >
        {entries.map(entry => {
          const error = getValueError(entry.value.trim(), duplicateValues);

          return (
            <EntryRow
              key={entry.id}
              entry={entry}
              error={error}
              placeholder={placeholder}
              onValueChange={next => handleValueChange(entry.id, next)}
              onRemove={() => handleRemove(entry.id)}
            />
          );
        })}
      </EntriesScrollArea>

      <AddEntryButton label={addLabel} onClick={handleAdd} />
    </div>
  );
};
